// Task 201 D2 / D4 — Domain binding CRUD hooks.
//
// These hooks drive the domain bindings sub-section on ManageProjectsPage.
// They follow the same mockup-mode / real-API duality as the other entity
// hooks: in mockup mode all mutations are no-ops and the query returns the
// fixture list embedded in the Project entity (or empty when none provided).
//
// Tenant isolation is server-enforced; the client only needs to pass the
// correct orgId (from currentOrg) in the URL path.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import type { ProjectDomainBinding } from '@/types/entities';

const DOMAIN_BINDING_FIXTURE_KEY = '__domain-bindings-fixture__';

// ── List ──────────────────────────────────────────────────────────────────────

interface UseDomainBindingsResult {
  data: readonly ProjectDomainBinding[];
  isLoading: boolean;
}

/**
 * Fetches the domain bindings for a project.
 *
 * In mockup mode the hook returns `mockBindings` (from the fixture). In live
 * mode it GETs `/api/v1/orgs/:orgId/projects/:projectId/domain-bindings`.
 */
export function useDomainBindings(
  projectId: string,
  mockBindings?: readonly ProjectDomainBinding[],
): UseDomainBindingsResult {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  const query = useQuery({
    queryKey: ['domain-bindings', orgId, projectId],
    queryFn: async () => {
      const token = await getToken();
      const res = await apiFetch<{ bindings: ProjectDomainBinding[] }>(
        `/api/v1/orgs/${orgId}/projects/${projectId}/domain-bindings`,
        token,
      );
      return res.bindings;
    },
    enabled: !mockupMode && !!orgId && !!projectId,
  });

  if (mockupMode) return { data: mockBindings ?? [], isLoading: false };
  return { data: query.data ?? [], isLoading: query.isLoading };
}

// ── Create ────────────────────────────────────────────────────────────────────

/**
 * Mutation hook to create a domain binding for a project.
 *
 * On success invalidates the `domain-bindings` query for the project.
 * In mockup mode the mutation is a no-op.
 */
export function useCreateDomainBinding(projectId: string) {
  const { currentOrg, mockupMode } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (body: { host: string; kind: 'exact' | 'suffix'; confidence: number }) => {
      if (mockupMode) return undefined as unknown as ProjectDomainBinding;
      const token = await getToken();
      return apiFetch<ProjectDomainBinding>(
        `/api/v1/orgs/${currentOrg.id}/projects/${projectId}/domain-bindings`,
        token,
        {
          method: 'POST',
          body: JSON.stringify(body),
        },
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['domain-bindings', currentOrg.id, projectId] });
    },
  });
}

// ── Delete ────────────────────────────────────────────────────────────────────

/**
 * Mutation hook to delete a domain binding by id.
 *
 * On success invalidates the `domain-bindings` query for the project.
 * In mockup mode the mutation is a no-op.
 */
export function useDeleteDomainBinding(projectId: string) {
  const { currentOrg, mockupMode } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (bindingId: string) => {
      if (mockupMode) return undefined;
      const token = await getToken();
      return apiFetch<undefined>(
        `/api/v1/orgs/${currentOrg.id}/projects/${projectId}/domain-bindings/${bindingId}`,
        token,
        { method: 'DELETE' },
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['domain-bindings', currentOrg.id, projectId] });
    },
  });
}

// Suppress unused-import for the fixture key constant used by tests only.
export { DOMAIN_BINDING_FIXTURE_KEY };
